import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  ContentCopy as CopyIcon,
  Lock as LockIcon,
  Public as GlobalIcon,
  LocationCity as MunicipioIcon,
} from '@mui/icons-material';
import { perfisService, municipiosService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Perfil {
  id: number;
  nome: string;
  descricao: string;
  permissoes: number[];
  municipio_id: number | null;
  is_default: boolean;
}

interface Permissao {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
  escopo: string;
}

interface Municipio {
  id: number;
  codigo_ibge: string;
  nome: string;
}

const PerfisPage: React.FC = () => {
  const { user, municipioAtivo, hasPermission } = useAuth();
  const isSysAdmin = user?.is_admin || hasPermission('SYSTEM_ADMIN');

  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [permissoes, setPermissoes] = useState<Permissao[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [editingPerfil, setEditingPerfil] = useState<Perfil | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 = municipio ativo, 1 = globais

  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    permissoes: [] as number[],
    municipio_id: null as number | null,
  });

  const [copyData, setCopyData] = useState({
    source_municipio_id: null as number | null,
    target_municipio_id: null as number | null,
  });

  useEffect(() => {
    loadMunicipios();
    loadPermissoes();
  }, []);

  useEffect(() => {
    loadPerfis();
  }, [municipioAtivo]);

  const loadMunicipios = async () => {
    try {
      if (isSysAdmin) {
        const data = await municipiosService.getMunicipios();
        setMunicipios(data);
      } else {
        const data = await municipiosService.getMunicipiosByUser();
        setMunicipios(data);
      }
    } catch (err) {
      setError('Erro ao carregar municipios');
    }
  };

  const loadPerfis = async () => {
    try {
      const data = await perfisService.getPerfis(municipioAtivo?.id);
      setPerfis(data);
    } catch (err) {
      setError('Erro ao carregar perfis');
    }
  };

  const loadPermissoes = async () => {
    try {
      const data = await perfisService.getPermissoes();
      setPermissoes(data);
    } catch (err) {
      setError('Erro ao carregar permissoes');
    }
  };

  // Separar perfis globais e municipais
  const perfisGlobais = useMemo(() => perfis.filter(p => p.municipio_id === null), [perfis]);
  const perfisMunicipais = useMemo(() => perfis.filter(p => p.municipio_id !== null), [perfis]);

  const canEditPerfil = (perfil: Perfil): boolean => {
    if (isSysAdmin) return true;
    // Coordenadores não podem editar perfis globais
    if (perfil.municipio_id === null) return false;
    return true;
  };

  const canDeletePerfil = (perfil: Perfil): boolean => {
    if (isSysAdmin) return true;
    if (perfil.municipio_id === null) return false;
    if (perfil.is_default) return false;
    return true;
  };

  const handleOpenDialog = (perfil?: Perfil) => {
    if (perfil) {
      setEditingPerfil(perfil);
      setFormData({
        nome: perfil.nome,
        descricao: perfil.descricao,
        permissoes: perfil.permissoes,
        municipio_id: perfil.municipio_id,
      });
    } else {
      setEditingPerfil(null);
      setFormData({
        nome: '',
        descricao: '',
        permissoes: [],
        municipio_id: isSysAdmin ? null : (municipioAtivo?.id || null),
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingPerfil) {
        await perfisService.updatePerfil(editingPerfil.id, formData);
        setSuccess('Perfil atualizado com sucesso!');
      } else {
        await perfisService.createPerfil(formData);
        setSuccess('Perfil criado com sucesso!');
      }
      setOpenDialog(false);
      loadPerfis();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar perfil');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este perfil?')) return;

    try {
      await perfisService.deletePerfil(id);
      setSuccess('Perfil excluido com sucesso!');
      loadPerfis();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir perfil');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleCopyPerfis = async () => {
    if (!copyData.target_municipio_id) {
      setError('Selecione o municipio de destino');
      return;
    }
    try {
      await perfisService.copyPerfis({
        source_municipio_id: copyData.source_municipio_id,
        target_municipio_id: copyData.target_municipio_id,
      });
      setSuccess('Perfis copiados com sucesso!');
      setOpenCopyDialog(false);
      setCopyData({ source_municipio_id: null, target_municipio_id: null });
      loadPerfis();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao copiar perfis');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleTogglePermissao = (permissaoId: number) => {
    setFormData((prev) => ({
      ...prev,
      permissoes: prev.permissoes.includes(permissaoId)
        ? prev.permissoes.filter((id) => id !== permissaoId)
        : [...prev.permissoes, permissaoId],
    }));
  };

  const groupPermissoesByEscopo = () => {
    const grouped: { [key: string]: Permissao[] } = {};
    permissoes.forEach((p) => {
      if (!grouped[p.escopo]) {
        grouped[p.escopo] = [];
      }
      grouped[p.escopo].push(p);
    });
    return grouped;
  };

  const renderPerfilCard = (perfil: Perfil) => {
    const isGlobal = perfil.municipio_id === null;
    const editable = canEditPerfil(perfil);
    const deletable = canDeletePerfil(perfil);

    return (
      <Grid item xs={12} md={6} key={perfil.id}>
        <Card sx={{ opacity: !editable ? 0.85 : 1, position: 'relative' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="h6">
                    {perfil.nome}
                  </Typography>
                  {isGlobal && (
                    <Chip
                      icon={<GlobalIcon sx={{ fontSize: 14 }} />}
                      label="Global"
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                    />
                  )}
                  {perfil.is_default && (
                    <Chip
                      label="Padrao"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {perfil.descricao}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {editable ? (
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(perfil)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Tooltip title="Perfil global - somente leitura">
                    <IconButton size="small" disabled>
                      <LockIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {deletable && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(perfil.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" gutterBottom>
              Permissoes ({perfil.permissoes.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {perfil.permissoes.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Nenhuma permissao atribuida
                </Typography>
              ) : (
                perfil.permissoes
                  .map((permId) => {
                    const permissao = permissoes.find((p) => p.id === permId);
                    return permissao ? { id: permId, nome: permissao.nome } : null;
                  })
                  .filter((p) => p !== null)
                  .map((p) => (
                    <Chip
                      key={p!.id}
                      label={p!.nome}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 500, '& .MuiChip-label': { px: 1 } }}
                    />
                  ))
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>Gerenciar Perfis</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure perfis de acesso e suas permissoes
              {!isSysAdmin && municipioAtivo && (
                <> — {municipioAtivo.nome}</>
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={() => {
              setCopyData({ source_municipio_id: null, target_municipio_id: municipioAtivo?.id || null });
              setOpenCopyDialog(true);
            }}
          >
            Copiar Perfis
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Perfil
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs para separar perfis */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<MunicipioIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Municipio${municipioAtivo ? ` (${municipioAtivo.nome})` : ''}`}
            sx={{ textTransform: 'none', minHeight: 48 }}
          />
          <Tab
            icon={<GlobalIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Globais (${perfisGlobais.length})`}
            sx={{ textTransform: 'none', minHeight: 48 }}
          />
        </Tabs>
      </Paper>

      {/* Perfis municipais */}
      {tabValue === 0 && (
        <>
          {perfisMunicipais.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <MunicipioIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" gutterBottom>
                Nenhum perfil especifico para este municipio
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Voce pode criar um novo perfil ou copiar os perfis padrao
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={() => {
                    setCopyData({ source_municipio_id: null, target_municipio_id: municipioAtivo?.id || null });
                    setOpenCopyDialog(true);
                  }}
                >
                  Copiar Perfis Padrao
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                  Criar Perfil
                </Button>
              </Box>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {perfisMunicipais.map((perfil) => renderPerfilCard(perfil))}
            </Grid>
          )}
        </>
      )}

      {/* Perfis globais */}
      {tabValue === 1 && (
        <>
          {!isSysAdmin && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Perfis globais sao somente leitura. Para personalizar, copie-os para seu municipio.
            </Alert>
          )}
          {perfisGlobais.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <GlobalIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                Nenhum perfil global cadastrado
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {perfisGlobais.map((perfil) => renderPerfilCard(perfil))}
            </Grid>
          )}
        </>
      )}

      {/* Dialog Criar/Editar Perfil */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPerfil ? 'Editar Perfil' : 'Novo Perfil'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
            <TextField
              label="Nome do Perfil"
              fullWidth
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Ex: Coordenador Municipal"
              size="small"
            />
            <TextField
              label="Descricao"
              fullWidth
              multiline
              rows={2}
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descreva as responsabilidades deste perfil"
              size="small"
            />

            {/* Municipio - apenas para admin global */}
            {isSysAdmin && (
              <Autocomplete
                options={[{ id: 0, nome: 'Global (todos os municipios)', codigo_ibge: '' } as any, ...municipios]}
                getOptionLabel={(opt: any) => opt.nome}
                value={
                  formData.municipio_id === null
                    ? { id: 0, nome: 'Global (todos os municipios)', codigo_ibge: '' }
                    : municipios.find(m => m.id === formData.municipio_id) || null
                }
                onChange={(_, val: any) => {
                  setFormData({ ...formData, municipio_id: val && val.id !== 0 ? val.id : null });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Municipio" size="small" />
                )}
                size="small"
              />
            )}

            {/* Municipio info para coordenadores */}
            {!isSysAdmin && municipioAtivo && (
              <Alert severity="info" icon={<MunicipioIcon />} sx={{ py: 0.5 }}>
                Este perfil sera vinculado ao municipio: <strong>{municipioAtivo.nome}</strong>
              </Alert>
            )}

            <Divider />

            <Typography variant="subtitle1" fontWeight={600}>Permissoes</Typography>

            {Object.entries(groupPermissoesByEscopo()).map(([escopo, perms]) => (
              <Box key={escopo}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                  {escopo}
                </Typography>
                <FormGroup>
                  {perms.map((permissao) => (
                    <FormControlLabel
                      key={permissao.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={formData.permissoes.includes(permissao.id)}
                          onChange={() => handleTogglePermissao(permissao.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {permissao.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {permissao.descricao}
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.nome.trim()}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Copiar Perfis */}
      <Dialog open={openCopyDialog} onClose={() => setOpenCopyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copiar Perfis entre Municipios</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Copie os perfis padrao (globais) ou de um municipio para outro.
              Perfis com mesmo nome no destino serao ignorados.
            </Typography>
            <Autocomplete
              options={[{ id: 0, nome: 'Perfis Padrao (globais)', codigo_ibge: '' } as any, ...municipios]}
              getOptionLabel={(opt: any) => opt.nome}
              value={
                copyData.source_municipio_id === null
                  ? { id: 0, nome: 'Perfis Padrao (globais)', codigo_ibge: '' }
                  : municipios.find(m => m.id === copyData.source_municipio_id) || null
              }
              onChange={(_, val: any) => {
                setCopyData({ ...copyData, source_municipio_id: val && val.id !== 0 ? val.id : null });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Origem" size="small" />
              )}
              size="small"
            />
            <Autocomplete
              options={municipios}
              getOptionLabel={(opt: Municipio) => opt.nome}
              value={municipios.find(m => m.id === copyData.target_municipio_id) || null}
              onChange={(_, val) => {
                setCopyData({ ...copyData, target_municipio_id: val?.id || null });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Destino" size="small" />
              )}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenCopyDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleCopyPerfis}
            variant="contained"
            startIcon={<CopyIcon />}
            disabled={!copyData.target_municipio_id}
          >
            Copiar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PerfisPage;
